import annalisaFabbriPhoto from 'assets/images/projectTeam/annalisa-fabbri.png'
import maheenAnsariPhoto from 'assets/images/projectTeam/maheen-ansari.png'
import shaynaKuperPhoto from 'assets/images/projectTeam/shayna-kuper.png'
import stavrosKakourosPhoto from 'assets/images/projectTeam/stavros-kakouros.png'

export interface DimensionCardBulletSegment {
  text: string
  bold?: boolean
}

export interface DimensionCard {
  title: string
  checks: DimensionCardBulletSegment[][]
  sourcesLabel: string
  sources: string[]
}

export const dimensionCards: DimensionCard[] = [
  {
    title: 'Consumer perception',
    checks: [
      [
        { text: 'Sense ' },
        { text: 'check consumer perception', bold: true },
        { text: ' on ' },
        { text: 'overall brand positioning clarity and sharpness', bold: true },
      ],
      [
        { text: 'Focus on leveraging ' },
        { text: 'long term brand / consumer imagery', bold: true },
        { text: ' to avoid mixing with shorter term commercial related issues' },
      ],
    ],
    sourcesLabel: 'Data/Info type and source',
    sources: ['BEACH data', 'Fundamental Human Tension format: Truth-Need-Tension', 'MDS'],
  },
  {
    title: 'Intl BVA Expert team + agency experts',
    checks: [
      [
        { text: 'Get notes from ' },
        { text: 'best-in-class professionals', bold: true },
        { text: ' on current status of BVA key elements' },
      ],
      [{ text: 'Leverage know-how and expertise of our own teams as well as our partners' }],
    ],
    sourcesLabel: 'Data/Info type and source',
    sources: [
      'BVA expert community and BVA 2.0 core team',
      'Extl agency partners (activated by BVA 2.0 core team)',
      'Intl subject matter experts (activated by BVA 2.0 core team)',
    ],
  },
  {
    title: 'New trends and insights conflicting with BVA',
    checks: [
      [
        { text: 'Evaluate if ' },
        { text: 'new insights/ info /landscape change', bold: true },
        { text: ' would imply some of the current BVA areas can be improved' },
      ],
      [{ text: 'Leverage new framework researches and focus on data to assess where we stand' }],
    ],
    sourcesLabel: 'Data/Info type and source',
    sources: [
      'Category teams (of evaluated BVA and others)',
      'Agency partners (Ex. WPP)',
      'Intl subject matter experts',
    ],
  },
]

export type ScaleColor = 'red' | 'yellow' | 'green'

export interface ScaleRange {
  color: ScaleColor
  label: string
}

export interface AssessmentScale {
  ranges: ScaleRange[]
  caption: string
}

export interface AssessmentSubRow {
  rowType: 'Consumer input' | 'Expert team' | 'New trends/insights'
  info: string[]
  scale: AssessmentScale
}

export type Pillar = 'Human Insight' | 'Product Edge' | 'Brand Edge'

export interface AssessmentPillarGroup {
  pillar: Pillar
  rows: AssessmentSubRow[]
}

const leadershipRadioIndexScale: AssessmentScale = {
  ranges: [
    { color: 'red', label: '<80' },
    { color: 'yellow', label: '80-120' },
    { color: 'green', label: '>120' },
  ],
  caption: 'Leadership Radio Index',
}

const qtlyTrendIndexScale: AssessmentScale = {
  ranges: [
    { color: 'red', label: '<80' },
    { color: 'yellow', label: '80-120' },
    { color: 'green', label: '>120' },
  ],
  caption: 'Qtly trend Index',
}

const scaleOfTen: AssessmentScale = {
  ranges: [
    { color: 'red', label: '1-4' },
    { color: 'yellow', label: '5-7' },
    { color: 'green', label: '>8' },
  ],
  caption: 'Scale of 1-10',
}

export const assessmentComponentsRows: AssessmentPillarGroup[] = [
  {
    pillar: 'Human Insight',
    rows: [
      {
        rowType: 'Consumer input',
        info: ['Meaningfulness (Affinity, Consideration, Meets Needs)'],
        scale: leadershipRadioIndexScale,
      },
      {
        rowType: 'Expert team',
        info: ['Your current insight is relevant, revealing and surprising?'],
        scale: scaleOfTen,
      },
      {
        rowType: 'New trends/insights',
        info: ['Is there a change on audience hierarchy that challenges your current BVA?'],
        scale: scaleOfTen,
      },
    ],
  },
  {
    pillar: 'Product Edge',
    rows: [
      {
        rowType: 'Consumer input',
        info: [
          'Do you have a perceived advantage vs competition in terms of product strength?',
          'Is your products strength stable or growing over time?',
        ],
        scale: leadershipRadioIndexScale,
      },
      {
        rowType: 'Expert team',
        info: ['Is product edge clear and differentiating vs category norms?'],
        scale: qtlyTrendIndexScale,
      },
      {
        rowType: 'New trends/insights',
        info: [
          'Is product edge aligned to reason to consume of need state?',
          'Any new tech / ingredients transforming the category?',
        ],
        scale: scaleOfTen,
      },
    ],
  },
  {
    pillar: 'Brand Edge',
    rows: [
      {
        rowType: 'Consumer input',
        info: [
          'Differentiation (Uniqueness, Sets Trends)',
          'Salient (Total Awareness, Spontaneous Awareness, Top of Mind)',
        ],
        scale: leadershipRadioIndexScale,
      },
      {
        rowType: 'Expert team',
        info: ['Is brand edge (not comms taglines) clear vs competition?'],
        scale: scaleOfTen,
      },
      {
        rowType: 'New trends/insights',
        info: ['Are new imagery from consumer flow challenging current BVA?'],
        scale: scaleOfTen,
      },
    ],
  },
]

export interface AssessmentSourceLine {
  text: string
  isLink?: boolean
  icon?: 'mail'
  linkUrl?: string
}

export interface AssessmentSourceGroup {
  reflectionElements: string[]
  sourceLines: AssessmentSourceLine[]
}

export const assessmentSourceGroups: AssessmentSourceGroup[] = [
  {
    reflectionElements: ['Fundamental Human Tension format: Truth-Need-Tension'],
    sourceLines: [
      {
        text: 'TNT Insight Outline',
        isLink: true,
        linkUrl:
          'https://onedrive.coca-cola.com/:p:/g/personal/skakouros_coca-cola_com/EetbzJrZRVFHrrsW6XbV0FoB3AJtYfRnpwhqjERlmMs99A?e=pqKnSy',
      },
    ],
  },
  {
    reflectionElements: [
      'Meaningfulness [Affinity, Consideration, Meets Needs]',
      'Differentiation [Uniqueness, Sets Trends]',
      'Salient [Total Awareness, Spontaneous Awareness, Top of Mind]',
    ],
    sourceLines: [
      {
        text: 'MDS Analysis Home - Power BI',
        isLink: true,
        linkUrl:
          'https://app.powerbi.com/groups/me/reports/0448f67c-144e-4a6b-a8d6-f822c85283e4/71a283f5cc3d18bcabca?experience=power-bi',
      },
      { text: 'Focus on scores in EP combos' },
    ],
  },
  {
    reflectionElements: ['Product Strength', 'Taste Superiority Assessment'],
    sourceLines: [
      { text: 'Work in Progress, will share soon' },
      {
        text: 'Taste Superiority - Power BI',
        isLink: true,
        linkUrl:
          'https://app.powerbi.com/links/D-6s0S7smZ?ctid=548d26ab-8caa-49e1-97c2-a1b1a06cc39c&pbi_source=linkShare&bookmarkGuid=b1750790-831c-4cad-a9c1-2dc21ac12365',
      },
    ],
  },
  {
    reflectionElements: ['Need states insights [Signature Product Experiences]'],
    sourceLines: [{ text: 'Contact Maheen Ansari for details', isLink: true, icon: 'mail' }],
  },
  {
    reflectionElements: ['Consumer Flow Imagery'],
    sourceLines: [
      {
        text: 'Consumer Flows',
        isLink: true,
        linkUrl:
          'https://4sightteam.sharepoint.com/sites/ForesightAssociates-Server/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FForesightAssociates%2DServer%2FShared%20Documents%2F1%2E%20Projects%2F1%2E%20TCCC%2F100%20Global%2FTCCC%201185%20%2D%20Global%20Consumer%20Flows%2F5%2E%20Shared&p=true&ct=1740055297040&or=Teams%2DHL&ga=1&LOF=1',
      },
    ],
  },
]

export type ApproachBulletSegment = { text: string; bold?: boolean } | { dots: ScaleColor[] }

export interface ApproachBulletItem {
  segments: ApproachBulletSegment[]
  subBullets?: string[]
}

export interface ApproachPanel {
  key: 'redo' | 'refresh' | 'translate'
  title: string
  threshold: { label: string; dotColors: ScaleColor[] }
  bullets: ApproachBulletItem[]
}

export const approachPanels: ApproachPanel[] = [
  {
    key: 'redo',
    title: 'REDO',
    threshold: { label: '>= 5', dotColors: ['red', 'yellow'] },
    bullets: [
      {
        segments: [
          { text: 'Run all exercises and set-up the proper learning plans', bold: true },
          {
            text: ' to re-think through all 3 elements of the BVA and its interconnections (even if an area is fully "green" do keep it in scope as other areas changing will push for a rework on all areas)',
          },
        ],
      },
      {
        segments: [
          { text: 'Set-up a ' },
          { text: 'cross functional team to help draft meaningful brand role', bold: true },
          { text: ' that can last for ages' },
        ],
      },
      {
        segments: [
          { text: 'Follow the principles of the "How To" guide', bold: true },
          { text: ' in order to be able to:' },
        ],
        subBullets: [
          'Focus equally on all 3 critical elements of the BVA framework',
          'Fiercely route the work in data',
          'Leverage expertise across the organization',
        ],
      },
    ],
  },
  {
    key: 'refresh',
    title: 'REFRESH',
    threshold: { label: '>= 3', dotColors: ['red', 'yellow'] },
    bullets: [
      {
        segments: [
          { text: 'If the ', bold: true },
          { dots: ['red', 'yellow'] },
          { text: ' are all in 1 area', bold: true },
          { text: ' (HI, Product, or Brand) focus on the ' },
          { text: 'exercises and learning plan of that area only', bold: true },
        ],
      },
      {
        segments: [
          { text: 'Once that is addressed run a ' },
          {
            text: 'quick sprint with the owners of other areas to see how this can change / impact other areas and meaningful brand role',
            bold: true,
          },
        ],
      },
      {
        segments: [
          { text: 'If the ' },
          { dots: ['red', 'yellow'] },
          {
            text: ' are spread across, evaluate internally the need of a redo or discuss as a team the one area and learning plan to prioritize for the refresh',
          },
        ],
      },
    ],
  },
  {
    key: 'translate',
    title: '"JUST" TRANSLATE IN NEW FRAMEWORK',
    threshold: { label: '< 3', dotColors: ['green'] },
    bullets: [
      {
        segments: [
          { text: 'In case where the vast majority is ' },
          { dots: ['green'] },
          { text: ' apply your current BVA thinking into the new 2.0 framework' },
        ],
      },
      {
        segments: [
          { text: 'Evaluate some pieces of new the framework that could help strengthen your current thinking' },
        ],
      },
    ],
  },
]

export interface ProjectTeamMember {
  name: string
  jobPosition: string
  email?: string
  photo: string
}

export const projectTeam: ProjectTeamMember[] = [
  { name: 'Stavros Kakouros', jobPosition: 'Job position', photo: stavrosKakourosPhoto },
  { name: 'Annalisa Fabbri', jobPosition: 'Job position', email: 'afabbri@coca-cola.com', photo: annalisaFabbriPhoto },
  { name: 'Maheen Ansari', jobPosition: 'Job position', photo: maheenAnsariPhoto },
  { name: 'Shayna Kuper', jobPosition: 'Job position', email: 'skuper@coca-cola.com', photo: shaynaKuperPhoto },
]
